import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { CLAUDE_MODELS, type MailLang } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useMailPreferences,
  useUpdateMailPreferences,
} from '@/hooks/useMailSettings';

interface FormValues {
  defaultModel: string;
  defaultLang: MailLang;
  softDeleteRetentionDays: number;
}

const defaults: FormValues = {
  defaultModel: CLAUDE_MODELS.sonnetDefault,
  defaultLang: 'ja',
  softDeleteRetentionDays: 30,
};

export function PreferencesSection() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useMailPreferences();
  const updateMutation = useUpdateMailPreferences();
  const form = useForm<FormValues>({ defaultValues: defaults });

  useEffect(() => {
    if (data)
      form.reset({
        ...defaults,
        ...data,
        softDeleteRetentionDays: data.softDeleteRetentionDays ?? 30,
      });
  }, [data, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      await updateMutation.mutateAsync({
        ...values,
        softDeleteRetentionDays: Number(values.softDeleteRetentionDays),
      });
      toast.success('環境設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-8">
      <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
        環境設定
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>デフォルトモデル</Label>
          <Select
            value={form.watch('defaultModel')}
            onValueChange={(v) => form.setValue('defaultModel', v)}
            disabled={!canEdit}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLAUDE_MODELS.sonnetDefault}>sonnet (デフォルト)</SelectItem>
              <SelectItem value={CLAUDE_MODELS.opus}>opus (量子場解析)</SelectItem>
              <SelectItem value={CLAUDE_MODELS.haiku}>haiku (軽量)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>デフォルト言語</Label>
          <Select
            value={form.watch('defaultLang')}
            onValueChange={(v) => form.setValue('defaultLang', v as MailLang)}
            disabled={!canEdit}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ja">日本語</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="zh">中文</SelectItem>
              <SelectItem value="ko">한국어</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="p-retention">ゴミ箱保持日数</Label>
          <Input
            id="p-retention"
            type="number"
            min={1}
            {...form.register('softDeleteRetentionDays')}
            disabled={!canEdit}
          />
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </div>
      )}
    </form>
  );
}
